'use client';

import type { Post } from '@/types/entities';
import { useSiteContext } from '@/context/SiteContext';
import type { AppSettings } from '@/utils/data/fetch-app-settings';
import {
	type FC,
	useMemo,
	useContext,
	createContext,
	type ReactNode,
} from 'react';
import type {
	SkimlinksWrapperConfig,
	TrackonomicsWrapperConfig,
} from '@themaven-net/commerce-shared';
import {
	AffiliateTagLevel,
	type PhoenixCommerce,
	type AffiliateTagWithLevel,
	type PhoenixTrackClickEvent,
	type CommerceConfigContextValues,
} from '@/types/commerce';

import { useAdService } from './AdServiceContext';
import { useAppSettings } from './AppSettingsContext';
import type { ClientSiteConfig } from '../temporary-site-config';
import { serializeExperiments } from '../utils/serialize-experiments';

export const CommerceConfigContext = createContext<
	CommerceConfigContextValues | undefined
>(undefined);

export const useCommerceConfig = () => {
	const context = useContext(CommerceConfigContext);

	if (!context) {
		throw new Error(
			'useCommerceConfig must be used within a CommerceConfigContextProvider',
		);
	}

	return context;
};

function buildTrackonomicsOptions(
	postData: Post | undefined,
	siteConfig: ClientSiteConfig,
	siteSettings: AppSettings['settings'] | undefined,
): TrackonomicsWrapperConfig | undefined {
	// DO NOT LET THIS PR THROUGH WITH THE ?? condition still there
	const trackonomicsSettings = siteSettings?.analytics?.trackonomics;

	if (
		trackonomicsSettings === undefined ||
		trackonomicsSettings.link_wrapping === false ||
		postData?.meta?.trackonomics_disabled === true
	) {
		return {
			disabled: true,
			vendor: 'Trackonomics',
		};
	}

	const values = Object.entries({
		author: postData?.authors[0]?.name ?? '',
		item_id: postData?.meta?.tempest_id ?? '',
		mc: siteConfig.channelKey,
		page_type: postData?.meta.page_type ?? 'article',
		pageUrl: postData?.link ?? document?.URL,
		section: postData?.primaryCategory?.name ?? '',
		site_id: siteConfig.siteId ?? 'unknownSiteId',
		siteCode: trackonomicsSettings.site_id ?? '',
	}).filter(([_key, val]) => val !== '');

	return {
		disabled: false,
		options: Object.fromEntries(values) as TrackonomicsWrapperConfig['options'],
		vendor: 'Trackonomics',
	};
}

function buildSkimlinksWrapperConfig(
	postData: Post | undefined,
	siteConfig: ClientSiteConfig,
): SkimlinksWrapperConfig | undefined {
	const skimConfig = siteConfig.affiliateVendors?.providers?.Skimlinks;
	if (
		skimConfig === undefined ||
		skimConfig?.isEnabled !== true ||
		typeof skimConfig?.id !== 'string' ||
		postData?.meta?.skimlinks_disabled === true
	) {
		return {
			disabled: true,
			params: {
				publisherId: 'Not to be used, disabled at site level',
			},
			vendor: 'Skimlinks',
		};
	}

	return {
		disabled: false,
		params: {
			publisherId: skimConfig.id,
		},
		vendor: 'Skimlinks',
	};
}

/**
 * @todo update when we have post level meta tags
 */
function buildAffiliateTagConfigs(
	_postData: Post | undefined,
	siteConfig: ClientSiteConfig,
): AffiliateTagWithLevel[] {
	const tagOutput: AffiliateTagWithLevel[] = [];
	const siteTag =
		siteConfig.affiliateVendors?.providers?.AmazonAffiliate?.defaultAffiliateId;

	// We currently only have site level tags
	if (siteTag !== undefined) {
		tagOutput.push({
			level: AffiliateTagLevel.Site,
			value: {
				affiliate: 'Amazon',
				tag: siteTag,
			},
		});
	}

	return tagOutput;
}

export const CommerceConfigContextProvider: FC<{
	children: ReactNode;
	permutive?: Permutive;
	phoenixTrackClickEvent?: PhoenixTrackClickEvent;
	phxCommerce?: PhoenixCommerce;
	postData?: Post;
}> = ({
	children,
	permutive,
	phoenixTrackClickEvent,
	phxCommerce,
	postData,
}) => {
	const appSettings = useAppSettings();
	const adConfig = useAdService();
	const siteSettings = appSettings?.settings;
	const { config } = useSiteContext();

	const contextValueBuilder = () => {
		const affiliateTags = buildAffiliateTagConfigs(postData, config);
		const skimlinks = buildSkimlinksWrapperConfig(postData, config);
		const trackonomics = buildTrackonomicsOptions(
			postData,
			config,
			siteSettings,
		);

		return {
			affiliateTags,
			amazonNca: postData?.meta?.amazon_nca_enabled ?? true,
			experiments: serializeExperiments(adConfig.experiments),
			permutive,
			phoenixTrackClickEvent,
			phxCommerce,
			skimlinks,
			trackonomics,
			viewCorrelator: adConfig.viewCorrelator?.toString() || '',
		};
	};

	return (
		<CommerceConfigContext.Provider
			// config and siteSettings won't change from context to context in the same client, post data will
			// eslint-disable-next-line react-hooks/exhaustive-deps
			value={useMemo(contextValueBuilder, [postData])}
		>
			{children}
		</CommerceConfigContext.Provider>
	);
};
